/**
 * Every screen the application has, in one place.
 *
 * A view is declared here and referenced by key everywhere else — the router,
 * the sidebar, the mobile tab bar and `ProtectedRoute` all read this table
 * rather than repeating a path string. The point is that a screen's path and
 * the conditions for reaching it are stated once.
 *
 * `permission` (a codename, or several meaning "any of them") and `anyRoles`
 * gate a view against `/auth/me/`: `canSeeView` consults them and every
 * existing view keeps working because not having them means not gating. The
 * backend remains the authority — hiding a screen here is a courtesy, and the
 * API still answers 403 to anything a role lacks.
 */
import type { CurrentUser } from '@/api/types';

/**
 * The codenames the frontend cares about, mirroring `seed_roles` in
 * kdoshsop-api. A new codename ships twice: registered for seeding back-end
 * side, declared here for the UI.
 */
export const PERMISSIONS = {
  productObservationsView: 'product_observations.view_productobservation',
  productObservationsViewAll: 'product_observations.view_all_productobservation',
  productObservationsAdd: 'product_observations.add_productobservation',
  productObservationsChange: 'product_observations.change_productobservation',
  productObservationsRegisterDestruction: 'product_observations.register_destruction',
  destructionBatchView: 'product_observations.view_destruction_batch',
  destructionTraceabilityView: 'product_observations.view_destruction_traceability',
  destructionTraceabilityPrint: 'product_observations.print_destruction_traceability_label',
  destructionReceive: 'destruction.receive_destruction',
  destructionView: 'destruction.view_destruction',
  destructionChangeTransferDate: 'destruction.change_transfer_date',
} as const;

/** The role codes the seed ships, mirroring `seed_roles` in kdoshsop-api. */
export const ROLES = {
  warehouse: 'warehouse',
  ticketCreator: 'ticket_creator',
  ticketCreatorSite: 'ticket_creator_site',
  management: 'management',
} as const;

export interface ViewDef {
  path: string;
  /** i18n key. A view without one is reachable but never appears in navigation. */
  labelKey?: string;
  /** Requires `is_staff`. Today that means "can also reach the Django admin". */
  staffOnly?: boolean;
  /**
   * Required permission(s). An array means "any of these" — the shared
   * warehouse bandeja is reachable with `view_productobservation` (own site)
   * or `view_all_productobservation` (whole history).
   */
  permission?: string | readonly string[];
  /**
   * Required role codes, in addition to the permission above. An array means
   * "any of these". Navigation is grouped by role (see `navigation.tsx`), and
   * `anyRoles` is what keeps a screen out of a role it was not built for even
   * when the permission overlaps with another role's.
   */
  anyRoles?: readonly string[];
}

export const VIEWS = {
  home: { path: '/', labelKey: 'nav.home' },
  productObservations: {
    path: '/productos-observados',
    labelKey: 'nav.productObservations',
    permission: PERMISSIONS.productObservationsAdd,
    anyRoles: [ROLES.ticketCreator, ROLES.ticketCreatorSite, ROLES.management],
  },
  warehouseProductObservations: {
    path: '/productos-observados/almacen',
    labelKey: 'nav.warehouseObservations',
    permission: [PERMISSIONS.productObservationsViewAll, PERMISSIONS.productObservationsView],
    anyRoles: [ROLES.warehouse, ROLES.management],
  },
  productObservationDetail: {
    path: '/productos-observados/:id',
    permission: [PERMISSIONS.productObservationsViewAll, PERMISSIONS.productObservationsView],
    anyRoles: [ROLES.warehouse, ROLES.ticketCreator, ROLES.ticketCreatorSite, ROLES.management],
  },
  destructionBlock: {
    path: '/productos-observados/bloques/:blockCode',
    permission: PERMISSIONS.destructionView,
    anyRoles: [ROLES.warehouse, ROLES.management],
  },
  unspscGuide: {
    path: '/desmedro/guia-unspsc',
    labelKey: 'nav.unspscGuide',
    anyRoles: [ROLES.warehouse, ROLES.ticketCreator, ROLES.ticketCreatorSite, ROLES.management],
  },
  destructionTraceability: {
    path: '/destruction/traceability',
    permission: PERMISSIONS.destructionTraceabilityView,
  },
  destructionReceipts: {
    path: '/desmedro/recepcion',
    labelKey: 'nav.destructionReceipts',
    permission: PERMISSIONS.destructionReceive,
    anyRoles: [ROLES.warehouse, ROLES.management],
  },
  sunatCommunicationBatches: {
    path: '/comunicaciones-sunat',
    labelKey: 'nav.sunatCommunicationBatches',
    permission: PERMISSIONS.destructionBatchView,
    anyRoles: [ROLES.management],
  },
  sunatCommunicationBatchDetail: {
    path: '/comunicaciones-sunat/:id',
    permission: PERMISSIONS.destructionBatchView,
    anyRoles: [ROLES.management],
  },
  changePassword: { path: '/cambiar-contrasena' },
} satisfies Record<string, ViewDef>;

export type ViewKey = keyof typeof VIEWS;

/** Views that can appear in navigation, i.e. the ones with a label. */
export type NavigableViewKey = {
  [K in ViewKey]: (typeof VIEWS)[K] extends { labelKey: string } ? K : never;
}[ViewKey];

function permissionNames(permission: string | readonly string[]): readonly string[] {
  return typeof permission === 'string' ? [permission] : permission;
}

/**
 * UI convenience only. The API remains the authority for every operation —
 * hiding a screen is a courtesy, not a control.
 */
export function canSeeView(user: CurrentUser | null, view: ViewDef): boolean {
  if (!user) return false;
  if (user.is_superuser) return true;
  if (view.staffOnly && !user.is_staff) return false;
  if (view.permission) {
    const needed = permissionNames(view.permission);
    if (!needed.some((codename) => user.permissions.includes(codename))) return false;
  }
  if (view.anyRoles && !view.anyRoles.some((code) => user.role_codes.includes(code))) return false;
  return true;
}
